import { useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { getRoomImagesByInternalService } from '~/services/AccommodationService';
import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import { fetchAccomPropertyImages, isPropertyNotMappedError } from '~/utils/accommodationPropertyImageSync';
import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import { patchPropertyRoomTypeImagesInCache } from './ImagesVideo/mediaCache';
import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  allSettledWithConcurrency,
  buildMediaImages,
  buildRoomImageReplacePayload,
  clearDeletedOfferImageSourceKeys,
  clearRemovedRoomImagePublicIds,
  dedupeMediaImages,
  findPersistedRoomByKey,
  getRoomKey,
  getStaleImportedOfferImageSourceKeys,
  getStaleImportedRoomImageSourceKeys,
  getSyncedRoomPublicIds,
} from './ImagesVideo/utils';
import type { RemovedRoomImageIdsByRoomKey } from './ImagesVideo/utils';

const IMAGE_IMPORT_CONCURRENCY = 5;
const SYNC_TOP_PROPERTY_IMAGE_COUNT = 15;
const SYNC_TOP_ROOM_IMAGE_COUNT = 10;
// Fetch more than the sync cap so images already on the room (which count against the
// backend's `limit` too) don't crowd out genuinely new ones once we dedupe client-side.
const SYNC_ROOM_FETCH_LIMIT = 40;
const ACCOMMODATION_INTERNAL_SERVICE = 'reservation';

export type SyncFailureReason = 'property_not_mapped' | 'room_not_mapped' | 'partial_import_failure' | 'other';

export interface SyncAllImagesSectionResult {
  label: string;
  syncedCount: number;
  failed: boolean;
  failureReason?: SyncFailureReason;
  errorMessage?: string;
}

export interface SyncAllImagesResult {
  sections: Array<SyncAllImagesSectionResult>;
}

function classifySyncError(error: unknown, notMappedReason: SyncFailureReason): SyncFailureReason {
  return isPropertyNotMappedError(error) ? notMappedReason : 'other';
}

interface UseOfferBuilderMediaImportParams {
  offerId: string;
  offerImages: Array<App.OfferImage>;
  propertyName?: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  mediaImages: Array<MediaImage>;
  setUploadedImages: (updater: (currentImages: Array<MediaImage>) => Array<MediaImage>) => void;
  setDeletedImageSourceKeys: (updater: (currentKeys: Array<string>) => Array<string>) => void;
  setRemovedRoomImageIdsByRoomKey: (
    updater: (current: RemovedRoomImageIdsByRoomKey) => RemovedRoomImageIdsByRoomKey,
  ) => void;
}

export interface OfferBuilderMediaImportController {
  importImages: (photoUnits: Array<{ id_cloudinary_external: string; title?: string }>) => Promise<number>;
  importRoomImages: (
    roomKey: string,
    photoUnits: Array<{ id_cloudinary_external: string; title?: string }>,
  ) => Promise<number>;
  syncTopRoomImages: (roomKey: string) => Promise<void>;
  syncAllImages: (propertyId: string | undefined) => Promise<SyncAllImagesResult>;
}

export default function useOfferBuilderMediaImport({
  offerId,
  offerImages,
  propertyName,
  propertyRoomTypes,
  mediaImages,
  setUploadedImages,
  setDeletedImageSourceKeys,
  setRemovedRoomImageIdsByRoomKey,
}: UseOfferBuilderMediaImportParams): OfferBuilderMediaImportController {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // Refs keep dedupe checks current across the network await in sync helpers below.
  const mediaImagesRef = useRef(mediaImages);
  mediaImagesRef.current = mediaImages;
  const propertyRoomTypesRef = useRef(propertyRoomTypes);
  propertyRoomTypesRef.current = propertyRoomTypes;

  const reportImportResult = (importedCount: number, failedCount: number) => {
    if (importedCount === 0 && failedCount === 0) {
      return;
    }

    if (importedCount === 0) {
      enqueueSnackbar(`Failed to import ${failedCount === 1 ? 'image' : `${failedCount} images`}`, {
        variant: 'error',
      });
      return;
    }

    const message = `Imported ${importedCount} ${importedCount === 1 ? 'image' : 'images'}`;

    enqueueSnackbar(failedCount > 0 ? `${message} (${failedCount} failed)` : message, {
      variant: failedCount > 0 ? 'warning' : 'success',
    });
  };

  const importImages = async (
    photoUnits: Array<{ id_cloudinary_external: string; title?: string }>,
    options: { silent?: boolean } = {},
  ): Promise<number> => {
    if (photoUnits.length === 0) {
      return 0;
    }

    try {
      const importResults = await allSettledWithConcurrency(photoUnits, IMAGE_IMPORT_CONCURRENCY, async (unit) => {
        const persistedOfferImage = await OffersService.createImage(
          {
            id_cloudinary_external: unit.id_cloudinary_external,
            title: unit.title,
          },
          offerId,
        );

        return {
          hidden: persistedOfferImage?.hidden,
          order: persistedOfferImage?.order,
          publicId: persistedOfferImage?.id_cloudinary_external ?? unit.id_cloudinary_external,
          sourceLabel: 'Offer gallery',
          sourceImageId: persistedOfferImage
            ? String(persistedOfferImage.id ?? persistedOfferImage.id_image)
            : undefined,
          sourceType: 'offer' as const,
          title: persistedOfferImage?.title || unit.title || undefined,
        } satisfies MediaImage;
      });
      const importedMediaImages = importResults.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value] : [],
      );
      const failedCount = importResults.length - importedMediaImages.length;

      if (failedCount > 0) {
        importResults.forEach((result) => {
          if (result.status === 'rejected' && result.reason instanceof Error) {
            reportError(result.reason);
          }
        });
      }

      const importedPublicIds = importedMediaImages.map((image) => image.publicId);
      setDeletedImageSourceKeys((currentKeys) => [
        ...new Set(
          clearDeletedOfferImageSourceKeys(currentKeys, importedPublicIds).concat(
            getStaleImportedOfferImageSourceKeys(offerImages, importedMediaImages),
          ),
        ),
      ]);
      setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(importedMediaImages)));

      if (!options.silent) {
        reportImportResult(importedMediaImages.length, failedCount);
      }

      return importedMediaImages.length;
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }

      if (!options.silent) {
        enqueueSnackbar(`Failed to import images: ${getErrorMessage(error)}`, { variant: 'error' });
      }

      return 0;
    }
  };

  const importRoomImages = async (
    roomKey: string,
    photoUnits: Array<{ id_cloudinary_external: string; title?: string }>,
    options: { silent?: boolean } = {},
  ): Promise<number> => {
    const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
    const propertyId = room?.property_id;
    const roomTypeId = room?.id;

    if (!propertyId || !roomTypeId) {
      if (!options.silent) {
        enqueueSnackbar("Can't import images: this room is not mapped to an accommodation room", {
          variant: 'error',
        });
      }

      return 0;
    }

    // replaceRoomTypeImages is a full replace; an in-flight upload has no sourceImageId yet and
    // would be silently dropped, so block until it persists or is removed.
    const hasPendingRoomUpload = mediaImages.some(
      (image) => image.sourceType === 'room' && image.sourceRoomId === roomKey && !image.sourceImageId,
    );

    if (hasPendingRoomUpload) {
      if (!options.silent) {
        enqueueSnackbar("Can't import images yet: wait for this room's upload in progress to finish", {
          variant: 'error',
        });
      }

      return 0;
    }

    try {
      // No incremental "attach" endpoint — resubmit existing room images alongside the imports.
      const fullImageSet = buildRoomImageReplacePayload({
        mediaImages,
        room,
        roomKey,
        photoUnits,
      });
      const importedPublicIds = new Set(photoUnits.map((unit) => unit.id_cloudinary_external));

      await ReservationService.replaceRoomTypeImages(fullImageSet, propertyId, roomTypeId);

      const getRoomTypeImagesResponse = await ReservationService.getRoomTypeImages(propertyId, roomTypeId);
      const refetchedRoomImages: Array<MediaSourceImage> = getRoomTypeImagesResponse.result ?? [];

      patchPropertyRoomTypeImagesInCache(queryClient, propertyId, roomTypeId, refetchedRoomImages);

      const importedMediaImages = buildMediaImages({
        offerImages: [],
        propertyRoomTypes: [{ ...room, images: refetchedRoomImages }],
      }).filter((image) => importedPublicIds.has(image.publicId));

      setRemovedRoomImageIdsByRoomKey((currentRemovedImagesByRoomKey) =>
        clearRemovedRoomImagePublicIds(currentRemovedImagesByRoomKey, roomKey, [...importedPublicIds]),
      );
      setDeletedImageSourceKeys((currentKeys) => [
        ...new Set(
          clearDeletedOfferImageSourceKeys(currentKeys, [...importedPublicIds]).concat(
            getStaleImportedRoomImageSourceKeys(propertyRoomTypes, roomKey, importedMediaImages),
          ),
        ),
      ]);
      setUploadedImages((currentImages) =>
        currentImages.filter((image) => !(image.sourceType === 'room' && image.sourceRoomId === roomKey)),
      );

      if (!options.silent) {
        reportImportResult(importedMediaImages.length, 0);
      }

      return importedMediaImages.length;
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }

      if (!options.silent) {
        enqueueSnackbar(`Failed to import images: ${getErrorMessage(error)}`, { variant: 'error' });
      }

      return 0;
    }
  };

  const syncTopPropertyImagesSilently = async (propertyId: string): Promise<SyncAllImagesSectionResult> => {
    try {
      // Dedupe against the ref in case a manual upload/import completed while this request was in flight.
      // Only count offer-gallery images against the offer cap — mediaImagesRef also holds room images.
      const existingOfferImages = mediaImagesRef.current.filter((image) => image.sourceType === 'offer');
      const existingPublicIds = new Set(existingOfferImages.map((image) => image.publicId));
      const remainingSlots = Math.max(0, SYNC_TOP_PROPERTY_IMAGE_COUNT - existingOfferImages.length);
      const images = await fetchAccomPropertyImages(propertyId, existingPublicIds);
      const photoUnits = images
        .filter((image) => !image.alreadyOnOffer)
        .slice(0, remainingSlots)
        .map((image) => ({ id_cloudinary_external: image.publicId, title: image.title }));

      const syncedCount = photoUnits.length > 0 ? await importImages(photoUnits, { silent: true }) : 0;

      if (syncedCount < photoUnits.length) {
        return {
          label: propertyName || 'the property',
          syncedCount,
          failed: true,
          failureReason: 'partial_import_failure',
        };
      }

      return { label: propertyName || 'the property', syncedCount, failed: false };
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }

      return {
        label: propertyName || 'the property',
        syncedCount: 0,
        failed: true,
        failureReason: classifySyncError(error, 'property_not_mapped'),
        errorMessage: getErrorMessage(error),
      };
    }
  };

  const syncTopRoomImagesSilently = async (roomKey: string): Promise<SyncAllImagesSectionResult> => {
    const room = findPersistedRoomByKey(propertyRoomTypes, roomKey);
    const label = room?.name || 'this room';

    if (!room?.property_id) {
      return { label, syncedCount: 0, failed: true, failureReason: 'property_not_mapped' };
    }

    if (!room?.id) {
      return { label, syncedCount: 0, failed: true, failureReason: 'room_not_mapped' };
    }

    try {
      const response = await getRoomImagesByInternalService({
        internalService: ACCOMMODATION_INTERNAL_SERVICE,
        internalServiceId: room.id,
        limit: SYNC_ROOM_FETCH_LIMIT,
      });
      // Re-derive against current mediaImages/propertyRoomTypes for the same reason as above.
      const currentRoom = findPersistedRoomByKey(propertyRoomTypesRef.current, roomKey) ?? room;
      const existingPublicIds = getSyncedRoomPublicIds(mediaImagesRef.current, currentRoom, roomKey);
      const remainingSlots = Math.max(0, SYNC_TOP_ROOM_IMAGE_COUNT - existingPublicIds.size);
      const photoUnits = (response.result?.images ?? [])
        .filter((image) => !existingPublicIds.has(image.imageId))
        .slice(0, remainingSlots)
        .map((image) => ({ id_cloudinary_external: image.imageId, title: image.title ?? undefined }));

      const syncedCount = photoUnits.length > 0 ? await importRoomImages(roomKey, photoUnits, { silent: true }) : 0;

      if (syncedCount < photoUnits.length) {
        return { label, syncedCount, failed: true, failureReason: 'partial_import_failure' };
      }

      return { label, syncedCount, failed: false };
    } catch (error) {
      if (error instanceof Error) {
        reportError(error);
      }

      return {
        label,
        syncedCount: 0,
        failed: true,
        failureReason: classifySyncError(error, 'room_not_mapped'),
        errorMessage: getErrorMessage(error),
      };
    }
  };

  const SYNC_FAILURE_MESSAGE_BY_REASON: Record<SyncFailureReason, (result: SyncAllImagesSectionResult) => string> = {
    property_not_mapped: (result) => `Can't sync images: ${result.label} is not mapped to an accommodation property`,
    room_not_mapped: (result) => `Can't sync images: ${result.label} is not mapped to an accommodation room`,
    partial_import_failure: (result) =>
      `Only synced ${result.syncedCount} image${result.syncedCount === 1 ? '' : 's'} from ${result.label}; some imports failed`,
    other: (result) => `Failed to sync images from ${result.label}: ${result.errorMessage ?? 'unknown error'}`,
  };

  const reportSyncResult = (result: SyncAllImagesSectionResult) => {
    if (result.failed) {
      const messageFor = SYNC_FAILURE_MESSAGE_BY_REASON[result.failureReason ?? 'other'];

      enqueueSnackbar(messageFor(result), { variant: 'error' });
    } else if (result.syncedCount === 0) {
      enqueueSnackbar(`0 new images synced from ${result.label}`, { variant: 'info' });
    } else {
      enqueueSnackbar(
        `Synced ${result.syncedCount} ${result.syncedCount === 1 ? 'image' : 'images'} from ${result.label}`,
        {
          variant: 'success',
        },
      );
    }
  };

  const syncTopRoomImages = async (roomKey: string) => {
    reportSyncResult(await syncTopRoomImagesSilently(roomKey));
  };

  const syncAllImages = async (propertyId: string | undefined): Promise<SyncAllImagesResult> => {
    const roomKeys = (propertyRoomTypes ?? []).map((room, index) => getRoomKey(room, index));
    const [propertyResult, ...roomResults] = await Promise.all([
      propertyId
        ? syncTopPropertyImagesSilently(propertyId)
        : Promise.resolve<SyncAllImagesSectionResult>({
            label: propertyName || 'the property',
            syncedCount: 0,
            failed: true,
            failureReason: 'property_not_mapped',
          }),
      ...roomKeys.map((roomKey) => syncTopRoomImagesSilently(roomKey)),
    ]);

    return { sections: [propertyResult, ...roomResults] };
  };

  return {
    importImages,
    importRoomImages,
    syncTopRoomImages,
    syncAllImages,
  };
}
